// Cardio progress from the history: distance, time and elevation per week and sport, time in heart rate
// zones, and the pace (or speed) trend of one sport. Pure functions over saved sessions.
import { addDays, localDay, weekStart, type Day } from '../habit/dates';
import type { CardioSport } from '../types';
import { activityProblem, type CardioActivity } from './activity';
import { paceSecPerKm } from './units';

/** The part of a saved session these functions read. */
export interface CardioSessionLike {
  id?: string;
  startedAt: number;
  activity?: CardioActivity;
}

/** The activity of a session when it has a valid one. */
export function activityOf(s: { activity?: unknown }): CardioActivity | null {
  return s.activity !== undefined && activityProblem(s.activity) === null ? (s.activity as CardioActivity) : null;
}

export interface CardioTotals {
  count: number;
  distanceM: number;
  durationSec: number;
  elevationGainM: number;
}

const empty = (): CardioTotals => ({ count: 0, distanceM: 0, durationSec: 0, elevationGainM: 0 });

function add(t: CardioTotals, a: CardioActivity): void {
  t.count += 1;
  t.distanceM += a.distanceM ?? 0;
  t.durationSec += a.durationSec;
  t.elevationGainM += a.elevationGainM ?? 0;
}

export interface CardioWeek {
  /** Monday of the week. */
  week: Day;
  total: CardioTotals;
  bySport: Partial<Record<CardioSport, CardioTotals>>;
  /** Seconds in zones 1 to 5 (activities with heart rate). */
  zoneSec: [number, number, number, number, number];
}

/** The last `weeks` weeks up to the week of `today`, oldest first, also weeks without cardio. */
export function cardioWeeks(sessions: CardioSessionLike[], opts: { today: Day; weeks: number; timeZone?: string }): CardioWeek[] {
  const last = weekStart(opts.today);
  const out: CardioWeek[] = [];
  for (let i = opts.weeks - 1; i >= 0; i--) out.push({ week: addDays(last, -7 * i), total: empty(), bySport: {}, zoneSec: [0, 0, 0, 0, 0] });
  const byWeek = new Map(out.map((w) => [w.week, w]));
  for (const s of sessions) {
    const a = activityOf(s);
    if (!a) continue;
    const w = byWeek.get(weekStart(localDay(s.startedAt, opts.timeZone)));
    if (!w) continue;
    add(w.total, a);
    add((w.bySport[a.sport] ??= empty()), a);
    a.zoneSec?.forEach((z, i) => { w.zoneSec[i]! += z; });
  }
  return out;
}

/** Totals per sport over all given sessions (or a day range, both ends included). */
export function cardioTotals(sessions: CardioSessionLike[], range: { from?: Day; to?: Day; timeZone?: string } = {}): Partial<Record<CardioSport, CardioTotals>> {
  const out: Partial<Record<CardioSport, CardioTotals>> = {};
  for (const s of sessions) {
    const a = activityOf(s);
    if (!a) continue;
    const day = localDay(s.startedAt, range.timeZone);
    if ((range.from && day < range.from) || (range.to && day > range.to)) continue;
    add((out[a.sport] ??= empty()), a);
  }
  return out;
}

export interface PacePoint {
  day: Day;
  /** Seconds per km; shown in the sport's own measure (min/km, min/100 m, km/h). */
  secPerKm: number;
  distanceM: number;
}

/** Pace of every activity of a sport that has a distance, oldest first (at most the last `limit`). */
export function paceTrend(sessions: CardioSessionLike[], sport: CardioSport, opts: { timeZone?: string; limit?: number } = {}): PacePoint[] {
  const points: Array<PacePoint & { at: number }> = [];
  for (const s of sessions) {
    const a = activityOf(s);
    if (!a || a.sport !== sport) continue;
    const pace = paceSecPerKm(a.distanceM, a.durationSec);
    if (pace === null) continue;
    points.push({ at: s.startedAt, day: localDay(s.startedAt, opts.timeZone), secPerKm: pace, distanceM: a.distanceM! });
  }
  points.sort((x, y) => x.at - y.at);
  return points.slice(-(opts.limit ?? 30)).map(({ at: _at, ...p }) => p);
}

/** Sports that occur in the history, most frequent first. */
export function sportsInHistory(sessions: CardioSessionLike[]): CardioSport[] {
  const counts = new Map<CardioSport, number>();
  for (const s of sessions) {
    const a = activityOf(s);
    if (a) counts.set(a.sport, (counts.get(a.sport) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1]).map(([sport]) => sport);
}
